import { z } from 'zod';

import { UuidSchema } from '../common/id/id';
import { makeTranslationsSchema } from '../common/translations/translations';

import { StableKeySchema } from './stable-key/stable-key';

/**
 * One choice of a `single_select` / `multi_select` question, resolved into the
 * reader's locale (N5).
 *
 * `value` is the stable slug an **answer** stores; `label` is what the member
 * reads. They are separate for the same reason a question has a key: staff
 * rewording "Never married" into "Single" must not orphan every answer that
 * chose it.
 */
export const QuestionOptionSchema = z
  .object({
    id: UuidSchema,
    questionId: UuidSchema,
    value: StableKeySchema,
    sortOrder: z.number().int().min(0),
    isActive: z.boolean(),
    /** The active locale's label. Absent translations degrade to the key, never to a blank row. */
    label: z.string(),
  })
  .strict();

export type QuestionOption = z.infer<typeof QuestionOptionSchema>;

/** The per-locale content of an option — the ar/en tabs of the builder form (W7). */
export const QuestionOptionTranslationsSchema = makeTranslationsSchema(
  z.object({ label: z.string().min(1).max(200) }).strict(),
);

export type QuestionOptionTranslations = z.infer<typeof QuestionOptionTranslationsSchema>;

/**
 * An option as the admin builder sees it: the row plus **every** locale, so the
 * edit form can prefill both tabs from one read (W7).
 */
export const QuestionOptionDetailSchema = QuestionOptionSchema.extend({
  translations: QuestionOptionTranslationsSchema,
}).strict();

export type QuestionOptionDetail = z.infer<typeof QuestionOptionDetailSchema>;

/**
 * Creating an option. Both locales are **required** (U8 key parity applied to
 * content): an option that exists in Arabic only is a blank choice for every
 * English reader, and the member cannot tell it apart from a bug.
 */
export const CreateQuestionOptionSchema = z
  .object({
    value: StableKeySchema,
    sortOrder: z.number().int().min(0).optional(),
    isActive: z.boolean().optional(),
    translations: QuestionOptionTranslationsSchema,
  })
  .strict();

export type CreateQuestionOptionRequest = z.infer<typeof CreateQuestionOptionSchema>;

/**
 * Updating an option — everything except `value`, which is **absent by
 * construction**: the slug is immutable (see `stable-key.ts`), and a schema that
 * cannot express the change is a stronger guarantee than a service that refuses
 * it.
 */
export const UpdateQuestionOptionSchema = z
  .object({
    sortOrder: z.number().int().min(0).optional(),
    isActive: z.boolean().optional(),
    translations: QuestionOptionTranslationsSchema.optional(),
  })
  .strict();

export type UpdateQuestionOptionRequest = z.infer<typeof UpdateQuestionOptionSchema>;
